<script lang="ts">
  import {
    settings,
    setTheme,
    setDensity,
    setAccent,
    type Theme,
    type Density,
    type Accent,
  } from '$domain/settings.svelte';

  let open = $state(false);
  let menuEl: HTMLDivElement | null = $state(null);

  function close(): void {
    open = false;
  }

  // Close on outside-click. The `composedPath` check covers events
  // dispatched from inside shadow DOM (none today but cheap insurance).
  function onDocClick(e: MouseEvent): void {
    if (!open) return;
    const path = (e.composedPath?.() ?? []) as Element[];
    if (menuEl && (path.includes(menuEl) || menuEl.contains(e.target as Node))) return;
    close();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  $effect(() => {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  });

  const themeOptions: { v: Theme; l: string; hint: string }[] = [
    { v: 'dark', l: 'Dark', hint: 'always dark' },
    { v: 'light', l: 'Light', hint: 'always light' },
    { v: 'auto', l: 'Auto', hint: 'match system' },
  ];
  const densityOptions: { v: Density; l: string }[] = [
    { v: 'compact', l: 'Compact' },
    { v: 'cozy', l: 'Cozy' },
    { v: 'comfortable', l: 'Comfortable' },
  ];
  const accentOptions: { v: Accent; l: string; sw: string }[] = [
    { v: 'solar', l: 'Solar', sw: 'oklch(0.74 0.18 47)' },
    { v: 'arc', l: 'Arc', sw: 'oklch(0.78 0.16 210)' },
    { v: 'void', l: 'Void', sw: 'oklch(0.7 0.18 300)' },
    { v: 'stasis', l: 'Stasis', sw: 'oklch(0.72 0.16 250)' },
  ];
</script>

<div class="settings-menu" bind:this={menuEl}>
  <button
    type="button"
    class="settings-trigger"
    onclick={() => (open = !open)}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-label="display settings"
    title="display settings"
  >
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 8a4 4 0 1 0 0 8a4 4 0 0 0 0-8m9.4 4l1.6 2.6l-2.6 1.6l-1.6-2.6c-.7.3-1.5.6-2.3.7l-.5 3h-3l-.5-3a8 8 0 0 1-2.3-.7l-1.6 2.6l-2.6-1.6l1.6-2.6a8 8 0 0 1-.7-2.3l-3-.5v-3l3-.5c.1-.8.4-1.6.7-2.3L1.4 3.4l2.6-1.6l1.6 2.6c.7-.3 1.5-.6 2.3-.7L8.4 1h3l.5 3c.8.1 1.6.4 2.3.7l1.6-2.6l2.6 1.6l-1.6 2.6c.3.7.6 1.5.7 2.3l3 .5v3l-3 .5c-.1.8-.4 1.6-.7 2.3"
        opacity="0.85"
      />
    </svg>
  </button>

  {#if open}
    <div class="settings-popover" role="dialog" aria-label="display settings">
      <section class="sm-group">
        <h4>Theme</h4>
        <div class="sm-row">
          {#each themeOptions as opt (opt.v)}
            <button
              type="button"
              class:active={settings.theme === opt.v}
              onclick={() => setTheme(opt.v)}
            >
              {opt.l}
            </button>
          {/each}
        </div>
      </section>

      <section class="sm-group">
        <h4>Density</h4>
        <div class="sm-row">
          {#each densityOptions as opt (opt.v)}
            <button
              type="button"
              class:active={settings.density === opt.v}
              onclick={() => setDensity(opt.v)}
            >
              {opt.l}
            </button>
          {/each}
        </div>
      </section>

      <section class="sm-group">
        <h4>Accent</h4>
        <div class="sm-row">
          {#each accentOptions as opt (opt.v)}
            <button
              type="button"
              class:active={settings.accent === opt.v}
              onclick={() => setAccent(opt.v)}
              title={opt.l}
              aria-label="{opt.l} accent"
            >
              <span class="sw" style="background: {opt.sw}"></span>
              <span class="sw-label">{opt.l}</span>
            </button>
          {/each}
        </div>
      </section>

      <p class="sm-hint">⌘/Ctrl+K opens command palette</p>
    </div>
  {/if}
</div>

<style>
  .settings-menu {
    position: relative;
  }
  .settings-trigger {
    background: transparent;
    border: 1px solid transparent;
    color: var(--fg-muted);
    padding: 6px;
    border-radius: var(--radius-2);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition:
      color var(--speed-fast) var(--easing),
      border-color var(--speed-fast) var(--easing);
  }
  .settings-trigger:hover {
    color: var(--fg);
    border-color: var(--border);
  }
  .settings-popover {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    min-width: 240px;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-3);
    padding: var(--space-3);
    z-index: var(--z-overlay);
    box-shadow: 0 16px 32px -12px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    animation: sm-in 140ms var(--easing);
  }
  @keyframes sm-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .sm-group h4 {
    margin: 0 0 var(--space-2);
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 500;
  }
  .sm-row {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }
  .sm-row button {
    flex: 1;
    min-width: 60px;
    background: var(--bg-elev-1);
    border: 1px solid var(--border);
    color: var(--fg-muted);
    padding: 6px 8px;
    border-radius: var(--radius-2);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: lowercase;
    cursor: pointer;
    transition: all var(--speed-fast) var(--easing);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .sm-row button:hover {
    color: var(--fg);
    border-color: var(--border-strong);
  }
  .sm-row button.active {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .sw {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid var(--border);
  }
  .sw-label {
    font-size: var(--text-xs);
  }
  .sm-hint {
    margin: 0;
    padding-top: var(--space-2);
    border-top: 1px dashed var(--border);
    color: var(--fg-subtle);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-align: center;
  }
</style>
